import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Globe, Scale, LogOut, UserCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

function LangSwitch() {
  const { locale, setLocale } = useI18n();
  return (
    <div
      className="inline-flex items-center border border-primary/20 rounded-sm overflow-hidden font-mono text-xs"
      data-testid="language-switcher"
    >
      <button
        type="button"
        onClick={() => setLocale("fr")}
        data-testid="lang-fr-btn"
        className={`px-2.5 py-1 transition-colors ${
          locale === "fr" ? "bg-primary text-white" : "text-primary hover:bg-primary/10"
        }`}
      >
        FR
      </button>
      <button
        type="button"
        onClick={() => setLocale("en")}
        data-testid="lang-en-btn"
        className={`px-2.5 py-1 transition-colors ${
          locale === "en" ? "bg-primary text-white" : "text-primary hover:bg-primary/10"
        }`}
      >
        EN
      </button>
    </div>
  );
}

export default function Header() {
  const { t, locale } = useI18n();
  const { user, logout, isAuthed } = useAuth();
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");
  
  // Check if user is a subscriber (not admin)
  const subToken = localStorage.getItem("lexhab_sub_token");
  const isSubscriber = !!subToken && !isAuthed;
  const subInfoRaw = localStorage.getItem("lexhab_sub_info");
  const subEmail = subInfoRaw ? JSON.parse(subInfoRaw).email : null;

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#F8F9FA]/85 border-b border-primary/10"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <div className="w-9 h-9 bg-primary text-white flex items-center justify-center rounded-sm">
            <Scale size={18} strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-semibold text-primary tracking-tight">
              Lexihab
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/60 -mt-0.5">
              Conseiller juridique · Cameroun
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `transition-colors ${
                isActive ? "text-primary font-medium" : "text-foreground/70 hover:text-primary"
              }`
            }
            data-testid="nav-home"
          >
            {t("nav.home")}
          </NavLink>
          <NavLink
            to="/chat"
            className={({ isActive }) =>
              `transition-colors ${
                isActive ? "text-primary font-medium" : "text-foreground/70 hover:text-primary"
              }`
            }
            data-testid="nav-chat"
          >
            {t("nav.chat")}
          </NavLink>
          <NavLink
            to="/pricing"
            className={({ isActive }) =>
              `transition-colors ${
                isActive ? "text-primary font-medium" : "text-foreground/70 hover:text-primary"
              }`
            }
            data-testid="nav-pricing"
          >
            {t("nav.pricing")}
          </NavLink>
          <NavLink
            to="/subscriber/login"
            className={({ isActive }) =>
              `transition-colors ${
                isActive ? "text-primary font-medium" : "text-foreground/70 hover:text-primary"
              }`
            }
            data-testid="nav-subscriber-login"
          >
            {t("nav.subscriberLogin")}
          </NavLink>
          <NavLink
            to={isAuthed ? "/admin" : "/admin/login"}
            className={({ isActive }) =>
              `transition-colors ${
                isAdminArea ? "text-primary font-medium" : "text-foreground/70 hover:text-primary"
              }`
            }
            data-testid="nav-admin"
          >
            {t("nav.admin")}
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch />
          
          {/* Subscriber account link */}
          {isSubscriber && (
            <Link
              to="/account"
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-light transition-colors px-2 py-1 border border-primary/20 rounded-sm bg-primary/5"
              data-testid="subscriber-account-btn"
            >
              <UserCircle size={14} />
              {subEmail ? subEmail.split('@')[0] : (locale === "fr" ? "Mon compte" : "My Account")}
            </Link>
          )}
          
          {isAuthed && (
            <button
              type="button"
              onClick={logout}
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-foreground/70 hover:text-primary transition-colors px-2 py-1 border border-transparent hover:border-primary/20 rounded-sm"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> {t("nav.logout")}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
